import { format } from 'd3-format';
import { useCallback } from 'react';
import { useWatch } from 'react-hook-form';

import { useSankeyFormContext } from 'contexts/sankeyFormContext';
import { useSankeyRenderContext } from 'contexts/sankeyRenderContext';
import { NrOfDecimalsEnum } from 'generated-api';

import { renderConfig } from '../constants';

// updateMarks: given a US-formatted number string, replace with user's
// preferred separators:
const updateMarks = (value: string) => {
  // If the digit-group mark is a comma, implicitly the decimal is a dot...
  // That's what we were given, so return:
  if (renderConfig.numberMarks.group === ',') {
    return value;
  }

  // Perform hacky mark swap using ! as a placeholder:
  return value
    .replaceAll(',', '!')
    .replaceAll('.', renderConfig.numberMarks.decimal)
    .replaceAll('!', renderConfig.numberMarks.group);
};

// formatDecimals: produce a value in the user's designated decimal format:
export function formatDecimals(value: number, decimalPlaces: number) {
  return updateMarks(format(`,.${decimalPlaces}f`)(value));
}

// Produce a value in the user's designated format
export function formatAmount(
  amount: number,
  prefix: string,
  suffix: string,
  decimalPlaces: number
) {
  const nString = formatDecimals(amount, decimalPlaces);
  return `${prefix}${nString}${suffix}`;
}

// Get the number of decimal places to use for formatting
// based on the user's selection in the form. If the user
// selected ALL, use the maxDecimalPlaces from the context.
export const getDecimalPlaces = (
  nrOfDecimals: SankeyFormValues['nr_of_decimals'] | undefined,
  maxDecimalPlaces: number
) => {
  return nrOfDecimals !== NrOfDecimalsEnum.ALL
    ? parseInt(nrOfDecimals ?? '0')
    : maxDecimalPlaces;
};

// Hook to get the number of decimal places based on form values
export const useDecimalPlaces = () => {
  const { maxDecimalPlaces } = useSankeyRenderContext();
  const { form } = useSankeyFormContext();
  const [nrOfDecimals] = useWatch({
    control: form.control,
    name: ['nr_of_decimals'],
  });
  return getDecimalPlaces(nrOfDecimals, maxDecimalPlaces);
};

// Hook around formatAmount
export const useAmountFormatter = () => {
  const { form } = useSankeyFormContext();
  const [unitPrefix, unitSuffix] = useWatch({
    control: form.control,
    name: ['unit_prefix', 'unit_suffix'],
  });
  const decimalPlaces = useDecimalPlaces();
  const amountFormatter = useCallback(
    (amount: number) =>
      formatAmount(amount, unitPrefix ?? '', unitSuffix ?? '', decimalPlaces),
    [unitPrefix, unitSuffix, decimalPlaces]
  );
  return amountFormatter;
};
